import { useState, useRef } from "react";
import { Upload, FileSpreadsheet, FileText, Image as ImageIcon, X, Globe, ClipboardPaste, Loader2, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ACCEPT, DEFAULT_OPTIONS } from "./intakeModel";

const TABS = [
  { id: "upload", label: "Upload files", icon: Upload },
  { id: "paste", label: "Paste or describe", icon: ClipboardPaste },
  { id: "website", label: "From your website", icon: Globe },
];

const fileIcon = (name) => (/\.(csv|xlsx|xls)$/i.test(name) ? FileSpreadsheet : /\.(png|jpe?g|webp|gif)$/i.test(name) ? ImageIcon : FileText);

export const SourceTabs = ({ tab, setTab, webCount }) => (
  <div className="flex gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/10 w-fit flex-wrap" data-testid="intake-tabs">
    {TABS.map(({ id, label, icon: Icon }) => (
      <button key={id} onClick={() => setTab(id)} data-testid={`intake-tab-${id}`}
        className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg transition-colors ${tab === id ? "bg-ism-teal/20 text-ism-teal" : "text-gray-400 hover:text-white"}`}>
        <Icon className="h-3.5 w-3.5" /> {label}
        {id === "website" && webCount > 0 && <span className="ml-1 text-[10px] px-1.5 rounded-full bg-emerald-500/20 text-emerald-300">{webCount}</span>}
      </button>
    ))}
  </div>
);

export const DefaultRelationship = ({ value, onChange }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="intake-default-relationship">
    <span className="text-xs text-gray-500">These are:</span>
    {DEFAULT_OPTIONS.map((o) => (
      <button key={o.id} onClick={() => onChange(o.id)} title={o.hint} data-testid={`intake-default-${o.id}`}
        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${value === o.id ? "bg-ism-teal/20 border-ism-teal/40 text-ism-teal" : "border-white/10 text-gray-400 hover:border-white/20"}`}>
        {o.label}
      </button>
    ))}
  </div>
);

export const UploadSource = ({ files, addFiles, removeFile }) => {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  return (
    <div className="space-y-3">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }} onClick={() => inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${drag ? "border-ism-teal bg-ism-teal/10" : "border-white/15 hover:border-white/30 bg-white/[0.02]"}`}
        data-testid="intake-dropzone">
        <Upload className="h-6 w-6 text-ism-teal mx-auto mb-2" />
        <p className="text-sm text-gray-200">Drop spreadsheets, CRM exports, PDFs, notes or screenshots</p>
        <p className="text-xs text-gray-500 mt-1">CSV, Excel, PDF, Word, text, images · any columns · we map them for you</p>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="intake-file-input" />
      </div>
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="intake-file-list">
          {files.map((f) => { const Icon = fileIcon(f.name); return (
            <li key={f.name} className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-200">
              <Icon className="h-3.5 w-3.5 text-ism-teal" /> {f.name}
              <button onClick={() => removeFile(f.name)} className="text-gray-500 hover:text-white" aria-label={`Remove ${f.name}`}><X className="h-3 w-3" /></button>
            </li>
          ); })}
        </ul>
      )}
    </div>
  );
};

export const PasteSource = ({ text, setText }) => (
  <div className="space-y-2">
    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid="intake-text-input"
      placeholder={"Anything works:\n• Acme Corp, Globex, Initech\n• acme.com\n• \"We signed Acme last spring; Jane Doe (CFO) is our champion. We'd love to win Globex and Hooli next year.\""}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:border-ism-teal/50 focus:outline-none resize-none" />
    <p className="text-xs text-gray-500">Company names, domains, people, or plain-English notes. The AI sorts out who is a customer and who you want to win.</p>
  </div>
);

export const WebsiteSource = ({ webItems, onAdd, mode }) => {
  const [picked, setPicked] = useState(() => new Set(webItems.map((w) => w.id)));
  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  if (!webItems.length) {
    return <p className="text-sm text-gray-500 py-4" data-testid="intake-website-empty">{mode === "stage" ? "No customer names were found on your website yet." : "No new customer names found on your website — anything found earlier is already in your accounts."}</p>;
  }
  return (
    <div className="space-y-3" data-testid="intake-website-list">
      <p className="text-xs text-gray-500">Companies your website names as customers (logos, testimonials, case studies). Untick any that aren&apos;t.</p>
      <div className="flex flex-wrap gap-2">
        {webItems.map((w) => (
          <button key={w.id} onClick={() => toggle(w.id)} title={w.notes} data-testid={`intake-web-${w.id}`}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${picked.has(w.id) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-white/10 text-gray-500"}`}>
            {picked.has(w.id) && <Check className="h-3 w-3" />} {w.companyName}
          </button>
        ))}
      </div>
      <Button size="sm" onClick={() => onAdd(webItems.filter((w) => picked.has(w.id)))} disabled={!picked.size} className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30" data-testid="intake-add-website-btn">
        Add {picked.size} as current customers
      </Button>
    </div>
  );
};

export const AnalyzeButton = ({ busy, stage, onClick, disabled }) => (
  <Button onClick={onClick} disabled={busy || disabled} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium rounded-xl" data-testid="intake-analyze-btn">
    {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {stage || "Working"}…</> : <><Sparkles className="h-4 w-4 mr-2" /> Read &amp; sort</>}
  </Button>
);
